import { type ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  useCurrentUser,
  type CurrentUser,
} from "@/features/auth/useCurrentUser";
import { useAuth } from "@/features/auth/useAuth";
import {
  ACCESS_RULES,
  canAccess,
  type AccessRule,
} from "@/features/auth/access";
import { useTheme } from "@/hooks/useTheme";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import NotificationBell from "@/components/notifications/NotificationBell";
import { ProfileAvatarMenu } from "./ProfileAvatarMenu";
import { ForcePasswordChangeModal } from "@/components/auth/ForcePasswordChangeModal";
import {
  Home,
  Truck,
  MoreHorizontal,
  ClipboardList,
  Package,
} from "lucide-react";
import "./AppShell.css";
import "./PageFrame.css";

const NAV_ITEMS: {
  label: string;
  to: string;
  icon: typeof Home;
  accessRule: AccessRule;
}[] = [
  { label: "Início", to: "/", icon: Home, accessRule: ACCESS_RULES.dashboard },
  {
    label: "Lista",
    to: "/lista",
    icon: ClipboardList,
    accessRule: ACCESS_RULES.supplierList,
  },
  {
    label: "Cotações",
    to: "/cotacoes",
    icon: ClipboardList,
    accessRule: ACCESS_RULES.quotation,
  },
  {
    label: "Pedidos",
    to: "/pedidos",
    icon: Package,
    accessRule: ACCESS_RULES.orders,
  },
  {
    label: "Cargas",
    to: "/cargas",
    icon: Truck,
    accessRule: ACCESS_RULES.checkin,
  },
  {
    label: "Mais",
    to: "/mais",
    icon: MoreHorizontal,
    accessRule: ACCESS_RULES.more,
  },
];

export function visibleNavItems(user: CurrentUser | null | undefined) {
  if (!user) return [];
  return NAV_ITEMS.filter((item) => canAccess(user, item.accessRule));
}

export function shouldShowNotificationBell(
  user: CurrentUser | null | undefined,
) {
  return canAccess(user, ACCESS_RULES.alerts);
}

function isActive(itemTo: string, currentPath: string) {
  return itemTo === "/" ? currentPath === "/" : currentPath.startsWith(itemTo);
}

function NavLink({
  item,
  active,
  mobile = false,
}: {
  item: (typeof NAV_ITEMS)[number];
  active: boolean;
  mobile?: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to as "/"}
      className={active ? "active" : undefined}
      aria-current={active ? "page" : undefined}
    >
      <Icon size={19} aria-hidden="true" />
      {mobile ? <span>{item.label}</span> : item.label}
    </Link>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { data: user } = useCurrentUser();
  const { logout, mustChangePassword } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const filteredNavItems = visibleNavItems(user);
  const passwordRequired =
    mustChangePassword || user?.must_change_password === true;

  return (
    <div className={`app-shell${theme === "dark" ? " is-dark" : ""}`}>
      <aside
        className="lnk-side-nav"
        aria-label="Navegação lateral"
        inert={passwordRequired}
      >
        <div className="lnk-logo">
          <img src="/logo.svg" alt="" />
          Elo<span>Link</span>
        </div>
        <nav aria-label="Navegação principal">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.to}
              item={item}
              active={isActive(item.to, currentPath)}
            />
          ))}
        </nav>
        {user && !isMobile && (
          <div className="lnk-user">
            <ProfileAvatarMenu
              name={user.name}
              theme={theme}
              onToggleTheme={toggleTheme}
              onLogout={logout}
            />
            <span>
              <strong>{user.name}</strong>
              <small>
                {user.role === "client"
                  ? "Cliente"
                  : user.role === "super"
                    ? "SUDO"
                    : user.role}
              </small>
            </span>
            {shouldShowNotificationBell(user) && <NotificationBell />}
          </div>
        )}
      </aside>

      <div className="app-shell-column" inert={passwordRequired}>
        <header className="app-shell-header">
          <div className="app-shell-mobile-logo">
            <img src="/logo.svg" alt="" />
            Elo<span>Link</span>
          </div>
          <div className="app-shell-header-actions">
            {isMobile && shouldShowNotificationBell(user) && (
              <NotificationBell />
            )}
            {isMobile && user && (
              <ProfileAvatarMenu
                name={user.name}
                theme={theme}
                onToggleTheme={toggleTheme}
                onLogout={logout}
              />
            )}
          </div>
        </header>
        <main
          className={`app-shell-main${currentPath === "/" ? " app-shell-main--dashboard" : ""}`}
        >
          {children}
        </main>
      </div>

      <nav
        className="lnk-mobile-nav"
        aria-label="Navegação móvel"
        inert={passwordRequired}
      >
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.to}
            item={item}
            active={isActive(item.to, currentPath)}
            mobile
          />
        ))}
      </nav>
      {passwordRequired && <ForcePasswordChangeModal />}
    </div>
  );
}
